/**
 * Shared helpers for the V2 section/question conditional-visibility controls.
 *
 * Both SectionTriggers (section-level `visibleWhen`) and QuestionVisibilityControls
 * (per-question `visibleWhen`) build the same "eligible source question" dropdown
 * and convert between the RHF `visibleWhen` value and a flat editable Draft. These
 * helpers were previously duplicated verbatim in both files; this module is the
 * single source of truth.
 *
 * NOTE: V1's `ConditionalVisibilityControls` is intentionally NOT a consumer — it
 * operates on a flat `IFormField` model rather than `SurveyPart`, so its
 * same-named helpers are a separate concern.
 */
import { NumericComparisonOperator } from '@ViewModels';
import {
	firingRangeForOperator,
	resolveNumericQuestionBounds,
	wholeThresholdEquivalent,
} from '../../../../../../../../surveys/utils/scaleQuestions';
import { NUMERIC_SOURCE_TYPES, OPTION_SOURCE_TYPES } from '../../types';
import type {
	CheckBoxesQuestion,
	DropDownQuestion,
	LinearScaleQuestion,
	NpsQuestion,
	QuestionVisibility,
	QuestionVisibilityComparison,
	QuestionVisibilityLogical,
	QuestionVisibilitySimple,
	SurveyPart,
	SurveySection,
} from '../../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OptionSource = DropDownQuestion | CheckBoxesQuestion;
export type NumericSource = LinearScaleQuestion | NpsQuestion;
export type EligibleSource = OptionSource | NumericSource;

const isEligibleSourceType = (type: string | undefined): boolean =>
	!!type && (OPTION_SOURCE_TYPES.has(type) || NUMERIC_SOURCE_TYPES.has(type));

export const isNumericSource = (question: { _type?: string } | undefined): question is NumericSource =>
	NUMERIC_SOURCE_TYPES.has(question?._type ?? '');

export const DEFAULT_COMPARISON_OPERATOR = NumericComparisonOperator.LessThanOrEqual;

export const COMPARISON_OPERATOR_OPTIONS: ReadonlyArray<{ value: NumericComparisonOperator; label: string }> = [
	{ value: NumericComparisonOperator.LessThan, label: 'less than' },
	{ value: NumericComparisonOperator.LessThanOrEqual, label: 'less than or equal to' },
	{ value: NumericComparisonOperator.GreaterThan, label: 'greater than' },
	{ value: NumericComparisonOperator.GreaterThanOrEqual, label: 'greater than or equal to' },
	{ value: NumericComparisonOperator.Equal, label: 'equal to' },
	{ value: NumericComparisonOperator.NotEqual, label: 'not equal to' },
];

export function resolveThresholdBounds(
	question: EligibleSource | undefined,
	operator: NumericComparisonOperator
): { min: number; max: number } | undefined {
	if (!isNumericSource(question)) return undefined;
	const range = resolveNumericQuestionBounds(question._type, question.minValue, question.maxValue);
	return range && firingRangeForOperator(range, operator);
}

export function clampThreshold(threshold: string, bounds?: { min: number; max: number }): string {
	const parsed = parseThreshold(threshold);
	if (parsed === null || !bounds) return threshold;
	if (parsed < bounds.min) return String(bounds.min);
	if (parsed > bounds.max) return String(bounds.max);
	return threshold;
}

/** A scale answer is a whole number, so a threshold is only comparable against one. */
export function parseWholeThreshold(threshold: string): number | null {
	const value = parseThreshold(threshold);
	return value !== null && Number.isInteger(value) ? value : null;
}

export const isThresholdCommittable = (threshold: string): boolean => parseWholeThreshold(threshold) !== null;

/** A comparison still being typed must not overwrite the stored rule with nothing. */
export const isDraftCommittable = (draft: Draft): boolean =>
	draft.mode !== 'comparison' || !draft.enabled || !draft.dependsOnFieldId || isThresholdCommittable(draft.threshold);

/** Why the box cannot be saved. An empty box is only an error once a rule has been stored. */
export const describeThresholdError = (
	draft: Draft,
	bounds: { min: number; max: number } | undefined,
	hasStoredRule: boolean
): string | undefined => {
	if (isDraftCommittable(draft)) return undefined;
	if (!draft.threshold.trim() && !hasStoredRule) return undefined;
	return bounds ? `Enter a whole number from ${bounds.min} to ${bounds.max}` : 'Enter a whole number';
};

export function parseThreshold(threshold: string): number | null {
	const trimmed = threshold.trim();
	if (!trimmed) return null;
	const parsed = Number(trimmed);
	return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A source-question entry with positional context, used to display a
 * "Section X · Question Y" label in the dropdown instead of falling back to the
 * raw fieldId UUID when the question has no human label yet.
 */
export interface SourceEntry {
	question: EligibleSource;
	/** Section index of this source question; -1 for flat (non-sectioned) parts. */
	sectionIndex: number;
	/** Question index within its section; -1 for flat parts. */
	questionIndex: number;
}

/**
 * A group of source entries sharing a section, in the shape Base UI's Combobox
 * expects for grouped `items` (`{ value, items }`). `value` doubles as the
 * group's display label.
 */
export interface SourceGroup {
	/** Display label for the group (also Base UI's group key). */
	value: string;
	items: SourceEntry[];
}

/**
 * Local edit-time mirror of the rule. The BE only accepts a single source
 * `fieldId` per `VisibleWhen` (it rejects with HTTP 400 otherwise), so the rule
 * is shaped as one source + N OR'd answer values.
 */
export type DraftMode = 'options' | 'comparison';

export interface Draft {
	enabled: boolean;
	dependsOnFieldId?: string;
	dependsOnOptionIds: string[];
	mode: DraftMode;
	operator: NumericComparisonOperator;
	threshold: string;
	/** A shape these controls can't author (e.g. a band). Round-trips verbatim, inputs disabled. */
	unsupported?: QuestionVisibility;
}

const emptyDraft = (): Draft => ({
	enabled: false,
	dependsOnFieldId: undefined,
	dependsOnOptionIds: [],
	mode: 'options',
	operator: DEFAULT_COMPARISON_OPERATOR,
	threshold: '',
});

// ---------------------------------------------------------------------------
// Eligible-source collection, labelling, grouping
// ---------------------------------------------------------------------------

/**
 * Collect the DropDownQuestion / CheckBoxesQuestion eligible to drive the target
 * part's visibility, with positional context for friendly labelling.
 *
 * Always includes every option question in the parts BEFORE `sectionIndex`
 * (descending into SurveySection containers; flat-legacy questions are taken
 * directly).
 *
 * When `questionIndex` is provided (question-level visibility) it ALSO includes
 * the earlier option questions within the target section
 * (`questions[0..questionIndex-1]`). When omitted (section-level visibility) the
 * in-section loop is skipped, so a section's triggers only see questions from
 * prior parts.
 */
export function buildEligibleSources(
	allSurveyParts: SurveyPart[],
	sectionIndex: number,
	questionIndex?: number
): SourceEntry[] {
	const result: SourceEntry[] = [];

	for (let i = 0; i < sectionIndex; i++) {
		const part = allSurveyParts[i];
		if (part?._type === 'SurveySection') {
			const section = part as SurveySection;
			const questions = section.questions ?? [];
			for (let j = 0; j < questions.length; j++) {
				const q = questions[j];
				if (isEligibleSourceType(q?._type)) {
					result.push({ question: q as EligibleSource, sectionIndex: i, questionIndex: j });
				}
			}
		} else if (isEligibleSourceType(part?._type)) {
			result.push({ question: part as EligibleSource, sectionIndex: -1, questionIndex: -1 });
		}
	}

	if (questionIndex !== undefined) {
		const current = allSurveyParts[sectionIndex];
		if (current?._type === 'SurveySection') {
			const section = current as SurveySection;
			const questions = section.questions ?? [];
			for (let j = 0; j < questionIndex; j++) {
				const q = questions[j];
				if (isEligibleSourceType(q?._type)) {
					result.push({ question: q as EligibleSource, sectionIndex, questionIndex: j });
				}
			}
		}
	}

	return result;
}

/**
 * Friendly label for a source entry shown inside the grouped Combobox. The
 * enclosing group label already carries the "Section X" context, so the per-item
 * text only needs the question's own position + label: "Question Y — <label>"
 * (or just "Question Y" when the question has no label yet). Legacy flat
 * questions (sectionIndex === -1) have no question index, so they fall back to
 * the bare label / "Question".
 */
export function describeSource(entry: SourceEntry): string {
	const base = entry.questionIndex >= 0 ? `Question ${entry.questionIndex + 1}` : 'Question';
	const label = entry.question.label?.trim();
	return label ? `${base} — ${label}` : base;
}

/**
 * Group eligible source entries by their owning section, preserving the original
 * order of `eligibleSources`. The group label is "Section {n} — {title}" (or just
 * "Section {n}" when the title is blank). Legacy flat questions (sectionIndex -1)
 * collect under a single "Questions" group.
 */
export function groupEligibleSources(eligibleSources: SourceEntry[], allSurveyParts: SurveyPart[]): SourceGroup[] {
	const groups: SourceGroup[] = [];
	const bySectionIndex = new Map<number, SourceGroup>();

	for (const entry of eligibleSources) {
		let group = bySectionIndex.get(entry.sectionIndex);
		if (!group) {
			let label: string;
			if (entry.sectionIndex < 0) {
				label = 'Questions';
			} else {
				const section = allSurveyParts[entry.sectionIndex] as SurveySection | undefined;
				const title = section?.title?.trim();
				label = title ? `Section ${entry.sectionIndex + 1} — ${title}` : `Section ${entry.sectionIndex + 1}`;
			}
			group = { value: label, items: [] };
			bySectionIndex.set(entry.sectionIndex, group);
			groups.push(group);
		}
		group.items.push(entry);
	}

	return groups;
}

// ---------------------------------------------------------------------------
// Draft <-> visibleWhen conversion
// ---------------------------------------------------------------------------

/**
 * Normalise the RHF `visibleWhen` value into a flat list of editable rows.
 */
export function fromVisibleWhen(visibleWhen: QuestionVisibility | undefined): Draft {
	if (!visibleWhen) {
		return emptyDraft();
	}
	if (visibleWhen._type === 'SimpleEqualityQuestionVisibility') {
		const simple = visibleWhen as QuestionVisibilitySimple;
		return {
			...emptyDraft(),
			enabled: true,
			dependsOnFieldId: simple.fieldId,
			dependsOnOptionIds: [simple.fieldValue],
		};
	}
	if (visibleWhen._type === 'ComparisonQuestionVisibility') {
		const comparison = visibleWhen as QuestionVisibilityComparison;
		return {
			...emptyDraft(),
			enabled: true,
			dependsOnFieldId: comparison.fieldId,
			mode: 'comparison',
			operator: comparison.op,
			threshold: String(comparison.value),
		};
	}
	const logical = visibleWhen as QuestionVisibilityLogical;
	const children = logical.visibilities ?? [];
	const comparisons = children.filter(
		(v): v is QuestionVisibilityComparison => v._type === 'ComparisonQuestionVisibility'
	);
	if (comparisons.length > 0) {
		const [first] = comparisons;
		const isAuthorable = children.length === 1;
		return {
			...emptyDraft(),
			enabled: true,
			dependsOnFieldId: first.fieldId,
			mode: 'comparison',
			operator: first.op,
			threshold: String(first.value),
			// Read-only stops the next commit dropping branches the controls cannot draw.
			...(isAuthorable ? {} : { unsupported: visibleWhen }),
		};
	}
	const simples = children.filter((v): v is QuestionVisibilitySimple => v._type === 'SimpleEqualityQuestionVisibility');
	return {
		...emptyDraft(),
		enabled: true,
		dependsOnFieldId: simples[0]?.fieldId,
		dependsOnOptionIds: simples.map(s => s.fieldValue),
	};
}

/**
 * Collapse the editable Draft back into the RHF `visibleWhen` shape:
 *   - 0 answers          → undefined
 *   - 1 answer           → SimpleEqualityQuestionVisibility
 *   - 2+ answers (OR'd)  → LogicalQuestionVisibility { logicalOperator: 'Or' }
 *   - comparison mode    → ComparisonQuestionVisibility (undefined until the threshold parses)
 */
export function toVisibleWhen(draft: Draft): QuestionVisibility | undefined {
	if (!draft.enabled || !draft.dependsOnFieldId) return undefined;
	if (draft.unsupported) return draft.unsupported;
	if (draft.mode === 'comparison') {
		const value = parseWholeThreshold(draft.threshold);
		if (value === null) return undefined;
		return {
			_type: 'ComparisonQuestionVisibility',
			fieldId: draft.dependsOnFieldId,
			op: draft.operator,
			value,
		} satisfies QuestionVisibilityComparison;
	}
	const values = draft.dependsOnOptionIds.filter(v => !!v);
	if (values.length === 0) return undefined;
	if (values.length === 1) {
		return {
			_type: 'SimpleEqualityQuestionVisibility',
			fieldId: draft.dependsOnFieldId,
			fieldValue: values[0],
		} satisfies QuestionVisibilitySimple;
	}
	return {
		_type: 'LogicalQuestionVisibility',
		logicalOperator: 'Or',
		visibilities: values.map(v => ({
			_type: 'SimpleEqualityQuestionVisibility',
			fieldId: draft.dependsOnFieldId!,
			fieldValue: v,
		})),
	} satisfies QuestionVisibilityLogical;
}

/** Applies a new operator, rounding then re-clamping the threshold into that operator's range. */
export const withOperator = (
	draft: Draft,
	question: EligibleSource | undefined,
	operator: NumericComparisonOperator
): Draft => {
	const parsed = parseThreshold(draft.threshold);
	if (parsed === null) return { ...draft, operator };
	const whole = wholeThresholdEquivalent(operator, parsed) ?? Math.round(parsed);
	return { ...draft, operator, threshold: clampThreshold(String(whole), resolveThresholdBounds(question, operator)) };
};

/** Reads a stored rule into a Draft, clamping the threshold so the browser cannot block Save. */
export function hydrateDraft(visibleWhen: QuestionVisibility | undefined, sources: SourceEntry[]): Draft {
	const draft = fromVisibleWhen(visibleWhen);
	if (draft.mode !== 'comparison' || draft.unsupported) return draft;
	const question = sources.find(source => source.question.fieldId === draft.dependsOnFieldId)?.question;
	return { ...draft, threshold: clampThreshold(draft.threshold, resolveThresholdBounds(question, draft.operator)) };
}

/**
 * Set of option ids currently picked by ANY OTHER answer row (excludes the
 * current row's own selection). Used to disable already-used options in each
 * row's dropdown so the same answer can't be added twice. Mirrors V1's
 * `computeUsedByOtherRows` in ConditionalVisibilityControls.
 */
export function computeUsedByOtherRows(dependsOnOptionIds: string[], currentRowIndex: number): Set<string> {
	const result = new Set<string>();
	dependsOnOptionIds.forEach((id, idx) => {
		if (idx !== currentRowIndex && id) result.add(id);
	});
	return result;
}
